import { useLazyQuery, useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { GridInputs } from "../../../components/Functional";
import { Button } from "../../../components/ui";
import { CREATE_SHIPMENT_ONWARD as createShipmentOnwardMutation } from "../../../graphql/mutation/Shipment";
import { FETCH_CONTACT as fetchContactQuery } from "../../../graphql/query/Contact";

const CreateOnwardShipmentBody = ({
  closeModal,
  shipmentId,
  countries,
  navigate,
}) => {
  const [onwardShipmentStage, setOnwardShipmentStage] = useState(0);

  const [
    createShipmentOnward,
    {
      error: createShipmentOnwardError,
      data: createShipmentOnwardData,
      reset: resetCreateShipmentOnward,
    },
  ] = useMutation(createShipmentOnwardMutation());

  const formik = useFormik({
    initialValues: {},
    enableReinitialize: true,
    onSubmit: values =>
      createShipmentOnward({
        variables: {
          input: {
            id: shipmentId,
            consigneeId: values?.contactId,
          },
        },
      }),
  });

  const [fetchContact] = useLazyQuery(fetchContactQuery());

  useEffect(() => {
    if (formik?.values?.contactId) {
      fetchContact({
        variables: {
          id: formik?.values?.contactId,
        },
      }).then(({ data }) => {
        formik.setFieldValue("name", data?.contact?.name);
        formik.setFieldValue("company", data?.contact?.company);
        formik.setFieldValue("address", data?.contact?.address);
        formik.setFieldValue("address2", data?.contact?.address2);
        formik.setFieldValue("address3", data?.contact?.address3);
        formik.setFieldValue("town", data?.contact?.town);
        formik.setFieldValue("county", data?.contact?.county);
        formik.setFieldValue("country", data?.contact?.country?.name);
        formik.setFieldValue("postcode", data?.contact?.postcode);
      });
    }
  }, [formik?.values?.contactId]);

  const inputs = {
    className: "grid grid-cols-1 md:grid-cols-2 gap-12",
    inputs: [
      {
        className: "grid grid-cols-1 gap-4",
        inputs: [
          { label: "", name: "contactId", type: "contactSearch" },
          { label: "Name", name: "name", type: "text" },
          { label: "Company", name: "company", type: "text" },
          { label: "Address", name: "address", type: "text" },
          { label: "", name: "address2", type: "text" },
          { label: "", name: "address3", type: "text" },
        ],
      },
      {
        className: "grid grid-cols-1 gap-4",
        inputs: [
          { label: "Town", name: "town", type: "text" },
          { label: "County", name: "county", type: "text" },
          {
            label: "Country",
            name: "country",
            type: "multi-select",
            options: countries?.map(item => ({
              label: item?.name,
              value: item?.id,
            })),
          },
          { label: "Postcode", name: "postcode", type: "text" },
        ],
      },
    ],
  };

  const inputProps = {
    ...inputs,
    formik,
  };

  if (
    createShipmentOnwardData &&
    !createShipmentOnwardData?.createShipmentOnward?.error
  ) {
    return (
      <div className="flex w-full flex-col gap-4 p-8">
        <div className="flex flex-row">
          <div className="flex flex-1 flex-col text-2xl font-medium">
            Create Onward Shipment
          </div>
          <div className="flex gap-4">
            <Button
              label={"Done"}
              onClick={() => {
                setOnwardShipmentStage(0);
                formik?.resetForm();
                navigate(
                  `/shipments/${createShipmentOnwardData?.createShipmentOnward?.shipment?.id}/details`,
                );
              }}
            />
          </div>
        </div>
        <div className="p-4">
          Onward shipment was created successfully. You will be redirected to
          the new shipment record.
        </div>
      </div>
    );
  }

  if (
    createShipmentOnwardError ||
    createShipmentOnwardData?.createShipmentOnward?.error
  ) {
    return (
      <div className="flex w-full flex-col gap-4 p-8">
        <div className="flex flex-row">
          <div className="flex flex-1 flex-col text-2xl font-medium">
            Create Onward Shipment
          </div>
          <div className="flex gap-4">
            <Button
              label={"Close"}
              onClick={() => {
                resetCreateShipmentOnward();
                closeModal?.();
                setOnwardShipmentStage(0);
                formik?.resetForm();
              }}
            />
          </div>
        </div>
        <div className="p-4">
          {createShipmentOnwardError?.message ||
            createShipmentOnwardData?.createShipmentOnward?.error}
        </div>
      </div>
    );
  }

  if (onwardShipmentStage === 1) {
    return (
      <div className="flex w-full flex-col gap-4 p-8">
        <div className="flex flex-row">
          <div className="flex flex-1 flex-col text-2xl font-medium">
            Create Onward Shipment
          </div>
          <div className="flex gap-4">
            <Button action="default" label="Cancel" onClick={closeModal} />
            <Button label={"Next"} onClick={formik?.submitForm} />
          </div>
        </div>
        <div>Select a delivery address for the onward shipment</div>
        <GridInputs {...inputProps} />
      </div>
    );
  }
  return (
    <div className="flex w-full flex-col gap-4 p-8">
      <div className="flex flex-row">
        <div className="flex flex-1 flex-col text-2xl font-medium">
          Create Onward Shipment
        </div>
        <div className="flex gap-4">
          <Button action="default" label="Cancel" onClick={closeModal} />
          <Button label={"Next"} onClick={() => setOnwardShipmentStage(1)} />
        </div>
      </div>
      <div className="p-4">
        This will create a shipment with all the warehouse items from the
        existing shipment attached. It does not follow the usual rules for item
        selection so you may end up with items that are difficult to remove if
        you do not really mean to shipment all the warehouse items. Are you sure
        you want to continue?
      </div>
    </div>
  );
};

export default CreateOnwardShipmentBody;
